import type { ICellRendererComp, ICellRendererParams } from 'ag-grid-community'
import { escapeHTML, syncCellTitle } from './dom'

/**
 * Fast Cell Renderer Utilities
 *
 * Two factories:
 * - `createFastRenderer` — writes `el.innerHTML` on init AND every refresh.
 *   Used by the locale-formatted text/number cells (column-processors) where the
 *   output is a single text node and re-parsing is trivial.
 * - `createTemplateRenderer` — parses the template ONCE, clones pre-parsed nodes
 *   per cell, and writes only dynamic fields on refresh (zero parse on scroll).
 *   Used by every structured cell renderer (badge/badges/button/stepper/progress/
 *   redirect/icon).
 *
 * SECURITY NOTE: Data comes from our own backend (Elixir/Phoenix) which already
 * sanitizes content. `escapeHTML` (re-exported for column-processors) provides
 * defense-in-depth.
 */

// Pre-computed wrapper class string for performance
const WRAPPER_CLASSES =
  'overflow-hidden w-full whitespace-nowrap h-full inline-flex items-center'
const TEXT_CLASSES =
  'min-w-0 w-full overflow-hidden text-ellipsis whitespace-nowrap'

const renderText = <T>(
  renderHTML: (data: T, params: ICellRendererParams) => string,
  data: T,
  params: ICellRendererParams
): string => `<span class="${TEXT_CLASSES}">${renderHTML(data, params)}</span>`

// Centering wrapper for template renderers: fill the cell box (full width + height)
// and vertically center the natural-height content. `w-full min-w-0` gives inner
// renderers a definite cell-width box to size against — badges/buttons fill it and
// ellipsize overflowing labels instead of being clipped by the cell. `overflow-hidden`
// is deliberately omitted so steppers/progress rings that slightly exceed the row
// height are never clipped; per-renderer overflow (e.g. `.grid-badge__label`) handles
// text ellipsis.
const CENTERING_WRAPPER_CLASSES = 'h-full w-full min-w-0 flex items-center'

/**
 * Fast cell renderer factory - creates lightweight renderers using innerHTML
 * Returns a class that implements ICellRendererComp with minimal overhead
 */
export const createFastRenderer = <T>(
  renderHTML: (data: T, params: ICellRendererParams) => string
): (new () => ICellRendererComp) => {
  return class FastRenderer implements ICellRendererComp {
    private el: HTMLDivElement | null = null

    init(params: ICellRendererParams): void {
      this.el = document.createElement('div')
      this.el.className = WRAPPER_CLASSES
      this.el.innerHTML = renderText(renderHTML, params.value as T, params)
      syncCellTitle(this.el, this.el, params.value)
    }

    getGui(): HTMLElement {
      return this.el!
    }

    refresh(params: ICellRendererParams): boolean {
      if (this.el) {
        this.el.innerHTML = renderText(renderHTML, params.value as T, params)
        syncCellTitle(this.el, this.el, params.value)
      }
      return true
    }

    destroy(): void {
      this.el = null
    }
  }
}

/**
 * Template-cloning renderer factory — the fastest renderer for fixed-structure
 * cells.
 *
 * AG Grid reuses renderer instances and calls `refresh(params)` repeatedly
 * during scroll. `createFastRenderer` re-parses an HTML string via
 * `el.innerHTML` on every refresh (hot path). This factory parses the template
 * exactly ONCE (at class-creation / module-load time) and thereafter only
 * clones the pre-parsed nodes per cell and writes dynamic fields — zero parsing
 * on scroll.
 *
 * The cloned content is appended into a full-cell-height centering `<div>`
 * (`getGui()` returns the WRAPPER), so template output is vertically centered in
 * the cell exactly like `createFastRenderer`'s text/number cells. The wrapper is
 * one plain div per cell — the same cost `createFastRenderer` always had.
 *
 * @param templateHTML - Static skeleton markup with a single root element. This
 *   becomes the wrapper's CHILD — the `update` target.
 * @param update - Mutates the cloned content in place from cell data. Must
 *   handle empty/null data (clear fields / hide) so an empty cell renders blank.
 *
 * @example
 * const Badge = createTemplateRenderer<BadgeData>(
 *   '<span class="grid-badge"><span class="grid-badge__label"></span></span>',
 *   (content, data) => {
 *     content.querySelector('.grid-badge__label')!.textContent =
 *       data?.label ?? ''
 *   }
 * )
 */
export const createTemplateRenderer = <T>(
  templateHTML: string,
  update: (content: HTMLElement, data: T, params: ICellRendererParams) => void
): (new () => ICellRendererComp) => {
  // The ONLY parse — happens once when this renderer class is created.
  const template = document.createElement('template')
  template.innerHTML = templateHTML

  const prototype = template.content.firstElementChild! as HTMLElement

  return class TemplateRenderer implements ICellRendererComp {
    // Wrapper returned by getGui() — stable across refreshes (centers the cell).
    private el: HTMLElement | null = null
    // Cloned template content — the update target, also stable across refreshes.
    private content: HTMLElement | null = null

    init(params: ICellRendererParams): void {
      const content = prototype.cloneNode(true) as HTMLElement

      update(content, params.value as T, params)

      const wrapper = document.createElement('div')
      wrapper.className = CENTERING_WRAPPER_CLASSES
      wrapper.appendChild(content)
      syncCellTitle(wrapper, content, params.value)

      this.content = content
      this.el = wrapper
    }

    getGui(): HTMLElement {
      return this.el!
    }

    refresh(params: ICellRendererParams): boolean {
      if (!(this.content && this.el)) {
        return false
      }

      update(this.content, params.value as T, params)
      syncCellTitle(this.el, this.content, params.value)

      return true
    }

    destroy(): void {
      this.el = null
      this.content = null
    }
  }
}

// Re-exported for column-processors' text/number cells (escapeHTML lives in dom.ts).
export { escapeHTML }
